import React, { useContext, useRef, useState } from "react";
import {
  Box,
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import useDownloadAttachment from "../../../hooks/useDownloadAttachment";
import AttachmentsContext from "../../../context/attachmentsContext";
import UserContext from "../../../context/userContext";
import { VIEWABLE_FILE_TYPES } from "../../../shared/constants/store";
import { getFileExtensionFromFileName } from "../../../shared/utils/getFileExtensionFromFileName";
import { deleteService } from "../../../services/axios/services";
import { toastError, toastSuccess } from "../../../utils/toast";

import { formatCultureHubDate } from "./CultureHubModal.utils";
import { formatDateLongPtBr } from "../../../utils/a11y/ptBr";
import visuallyHiddenSx from "../../../utils/a11y/visuallyHiddenSx";

const GRID_COLUMNS =
  "48px 176px minmax(220px, 1fr) 116px 146px 142px 90px";

const ASSESSMENT_GRID_COLUMNS = "48px 1fr 90px";

const ADDENDUM_GRID_COLUMNS =
  "48px minmax(260px, 1fr) 260px 220px 90px";

const MENU_ITEM_SX = {
  fontFamily: "inherit",
  fontWeight: 500,
  fontStyle: "normal",
  fontSize: "14px",
  color: "#3B3C3E",
  lineHeight: "16px",
  letterSpacing: "0.07em",
  verticalAlign: "middle",
};

const CultureHubRow = ({
  attachment,
  kind,
  selected,
  onToggle,
  contentId,
  onDeleteAssessment,
}) => {
  const [menuAnchorEl, setMenuAnchorEl] =
    useState(null);
  const [isDeletingAssessment, setIsDeletingAssessment] =
    useState(false);
  const isDeletingAssessmentRef = useRef(false);

  const { downloadAttachment } =
    useDownloadAttachment();
  const { setFileToView } = useContext(AttachmentsContext);
  const { hasPermission } = useContext(UserContext);

  const fileExtension = getFileExtensionFromFileName(
    attachment?.fileName
  );
  const canViewFile =
    VIEWABLE_FILE_TYPES.includes(fileExtension) &&
    attachment?.fileName !== null;

  const isMenuOpen = Boolean(menuAnchorEl);

  const handleOpenMenu = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const menuId = `culturehub-row-menu-${attachment.id}`;
  const buttonId = `${menuId}-button`;

  const optionsButtonProps = {
    id: buttonId,
    "aria-label": `Opções de ${attachment.fileName}`,
    "aria-haspopup": "menu",
    "aria-expanded": isMenuOpen ? "true" : undefined,
    "aria-controls": isMenuOpen ? menuId : undefined,
  };

  const menuProps = {
    id: menuId,
    anchorEl: menuAnchorEl,
    open: isMenuOpen,
    onClose: handleCloseMenu,
    MenuListProps: { "aria-labelledby": buttonId },
  };

  const handleDownload = () => {
    const fileToDownload = {
      fileData: attachment,
      contentId: 0,
      origin: "hub-cultura",
      mode: 0,
    };

    downloadAttachment(fileToDownload);
    handleCloseMenu();
  };

  const handleView = () => {
    if (!canViewFile) return;

    setFileToView({
      fileData: attachment,
      mode: 1,
      origin: "visualizador-hub-cultura",
      contentId,
      autoSave: false,
      newView: true,
      hideDownload: kind === "Assessment",
    });

    handleCloseMenu();
  };

  const handleViewInNewTab = async () => {
    if (!canViewFile) return;

    // opened synchronously so the popup blocker sees a user gesture; pointed at the file once it loads
    const newTab = window.open("", "_blank");

    handleCloseMenu();

    const response = await downloadAttachment({
      fileData: attachment,
      mode: 1,
      origin: "ficha-projeto",
      contentId,
      autoSave: false,
    });

    if (!response?.blob) {
      if (newTab) newTab.close();
      return;
    }

    // a PDF-typed blob makes the tab open the browser's PDF viewer instead of downloading
    const blob =
      fileExtension === "pdf" && response.blob.type !== "application/pdf"
        ? new Blob([response.blob], { type: "application/pdf" })
        : response.blob;
    const url = URL.createObjectURL(blob);

    if (newTab) {
      newTab.location.href = url;
    } else {
      window.open(url, "_blank");
    }

    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const handleDeleteAssessment = async () => {
    if (isDeletingAssessmentRef.current) return;

    isDeletingAssessmentRef.current = true;
    setIsDeletingAssessment(true);

    try {
      await deleteService({
        endpoint: `/assessments/${attachment.assessmentId}`,
        api: "v2",
      });

      toastSuccess("Parecer excluído com sucesso.");

      if (onDeleteAssessment) {
        onDeleteAssessment(attachment);
      }

      handleCloseMenu();
    } catch (error) {
      toastError("Erro ao excluir o parecer.");
    } finally {
      isDeletingAssessmentRef.current = false;
      setIsDeletingAssessment(false);
    }
  };

  const isAssessment = kind === "Assessment";
  const isAddendum = kind === "Addendum";
  const isOtherType = kind === "OtherType";

  /**
   * PARECER
   */
  if (isAssessment) {
    return (
      <Box
        role="row"
        sx={{
          minHeight: "56px",
          px: 1.5,
          display: "grid",
          gridTemplateColumns:
            ASSESSMENT_GRID_COLUMNS,
          alignItems: "center",
          borderBottom: "1px solid #E5E5E5",
        }}
      >
        <Box
          role="cell"
          sx={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <SelectionCheckbox
            attachment={attachment}
            selected={selected}
            onToggle={onToggle}
          />
        </Box>

        <Box role="cell">
          <Typography
            sx={{
              fontSize: "14px",
              color: "#333333",
            }}
          >
            {attachment.fileName}
          </Typography>

          {!!attachment.relatedNumbers?.length && (
            <Box
              sx={{
                mt: 0.5,
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 0.5,
              }}
            >
              <Typography
                sx={{
                  mr: 0.5,
                  fontSize: "10px",
                  color: "#929292",
                }}
              >
                {attachment.isEpisode
                  ? "Episódios relacionados:"
                  : "Capítulos relacionados:"}
              </Typography>

              {attachment.relatedNumbers.map(
                (number) => (
                  <Box
                    key={number}
                    component="span"
                    sx={{
                      px: 0.5,
                      py: 0.15,
                      borderRadius: "6px",
                      bgcolor: "#E9F7FE",
                      color: "#185CFB",
                      fontSize: "10px",
                    }}
                  >
                    <span aria-hidden="true">
                      {attachment.isEpisode ? "E" : "C"}
                      {String(number).padStart(2, "0")} - v
                      {attachment.version}
                    </span>
                    <Box component="span" sx={visuallyHiddenSx}>
                      {attachment.isEpisode ? "Episódio" : "Capítulo"}{" "}
                      {number}, versão {attachment.version}
                    </Box>
                  </Box>
                )
              )}
            </Box>
          )}
        </Box>

        <Box
          role="cell"
          sx={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <IconButton
            size="small"
            {...optionsButtonProps}
            onClick={handleOpenMenu}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>

          <Menu {...menuProps}>
            <MenuItem onClick={handleView} sx={MENU_ITEM_SX}>
              <VisibilityIcon
                fontSize="small"
                sx={{ mr: 1 }}
              />
              Visualizar
            </MenuItem>

            <MenuItem onClick={handleViewInNewTab} sx={MENU_ITEM_SX}>
              <OpenInNewIcon
                fontSize="small"
                sx={{ mr: 1 }}
              />
              Visualizar em nova aba
            </MenuItem>

            <MenuItem onClick={handleDownload} sx={MENU_ITEM_SX}>
              <DownloadIcon
                fontSize="small"
                sx={{ mr: 1 }}
              />
              Baixar
            </MenuItem>

            {hasPermission({ required: ["CultureHubAssessmentDelete"] }) && (
              <MenuItem
                onClick={handleDeleteAssessment}
                disabled={isDeletingAssessment}
                sx={MENU_ITEM_SX}
              >
                <DeleteOutlineIcon
                  fontSize="small"
                  sx={{ mr: 1 }}
                />
                Remover
              </MenuItem>
            )}
          </Menu>
        </Box>
      </Box>
    );
  }

  /**
   * ADENDO
   */
  if (isAddendum) {
    return (
      <Box
        role="row"
        sx={{
          minHeight: "44px",
          px: 1.5,
          display: "grid",
          gridTemplateColumns:
            ADDENDUM_GRID_COLUMNS,
          alignItems: "center",
          borderBottom: "1px solid #E5E5E5",
        }}
      >
        <Box
          role="cell"
          sx={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <SelectionCheckbox
            attachment={attachment}
            selected={selected}
            onToggle={onToggle}
          />
        </Box>

        <Typography
          role="cell"
          sx={{
            ...cellStyle,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={attachment.fileName}
        >
          {attachment.fileName}
        </Typography>

        <Typography
          role="cell"
          sx={{
            ...cellStyle,
            textAlign: "center",
          }}
        >
          {attachment.relatedNumbers?.length
            ? attachment.relatedNumbers
                .map((number) =>
                  String(number).padStart(2, "0")
                )
                .join(", ")
            : "-"}
        </Typography>

        <Typography
          role="cell"
          sx={{
            ...cellStyle,
            textAlign: "center",
          }}
        >
          <DateText
            value={attachment.cultureHubReleaseDate}
          />
        </Typography>

        <Box
          role="cell"
          sx={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <IconButton
            size="small"
            {...optionsButtonProps}
            onClick={handleOpenMenu}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>

          <Menu {...menuProps}>
            <MenuItem
              onClick={handleView}
              disabled={!canViewFile}
              sx={MENU_ITEM_SX}
            >
              <VisibilityIcon
                fontSize="small"
                sx={{ mr: 1 }}
              />
              {canViewFile ? "Visualizar" : "Visualizar (indisponível)"}
            </MenuItem>

            <MenuItem
              onClick={handleViewInNewTab}
              disabled={!canViewFile}
              sx={MENU_ITEM_SX}
            >
              <OpenInNewIcon
                fontSize="small"
                sx={{ mr: 1 }}
              />
              {canViewFile
                ? "Visualizar em nova aba"
                : "Visualizar em nova aba (indisponível)"}
            </MenuItem>

            <MenuItem onClick={handleDownload} sx={MENU_ITEM_SX}>
              <DownloadIcon
                fontSize="small"
                sx={{ mr: 1 }}
              />
              Baixar
            </MenuItem>
          </Menu>
        </Box>
      </Box>
    );
  }

  /**
   * CAPÍTULO / EPISÓDIO / OUTROS MATERIAIS
   */
  return (
    <Box
      role="row"
      sx={{
        minHeight: "44px",
        px: 1.5,
        display: "grid",
        gridTemplateColumns: GRID_COLUMNS,
        alignItems: "center",
        borderBottom: "1px solid #E5E5E5",
      }}
    >
      <Box
        role="cell"
        sx={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <SelectionCheckbox
          attachment={attachment}
          selected={selected}
          onToggle={onToggle}
        />
      </Box>

      <Typography
        role="cell"
        sx={{
          ...cellStyle,
          textAlign: "center",
        }}
      >
        {isOtherType
          ? attachment.type ?? "-"
          : attachment.number != null
          ? String(attachment.number).padStart(2, "0")
          : "-"}
      </Typography>

      <Typography
        role="cell"
        sx={{
          ...cellStyle,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={attachment.fileName}
      >
        {attachment.fileName}
      </Typography>

      <Typography role="cell" sx={cellStyle}>
        {attachment.version != null
          ? `${attachment.version}ª`
          : "-"}
      </Typography>

      <Typography role="cell" sx={cellStyle}>
        <DateText value={attachment.cultureHubReleaseDate} />
      </Typography>

      <Typography role="cell" sx={cellStyle}>
        <DateText
          value={attachment.diversityAssessmentPlanningDate}
        />
      </Typography>

      <Box
        role="cell"
        sx={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <IconButton
          size="small"
          {...optionsButtonProps}
          onClick={handleOpenMenu}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>

        <Menu {...menuProps}>
          <MenuItem
            onClick={handleView}
            disabled={!canViewFile}
            sx={MENU_ITEM_SX}
          >
            <VisibilityIcon
              fontSize="small"
              sx={{ mr: 1 }}
            />
            {canViewFile ? "Visualizar" : "Visualizar (indisponível)"}
          </MenuItem>

          <MenuItem
            onClick={handleViewInNewTab}
            disabled={!canViewFile}
            sx={MENU_ITEM_SX}
          >
            <OpenInNewIcon
              fontSize="small"
              sx={{ mr: 1 }}
            />
            {canViewFile
              ? "Visualizar em nova aba"
              : "Visualizar em nova aba (indisponível)"}
          </MenuItem>

          <MenuItem onClick={handleDownload} sx={MENU_ITEM_SX}>
            <DownloadIcon
              fontSize="small"
              sx={{ mr: 1 }}
            />
            Baixar
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};

const cellStyle = {
  fontSize: "14px",
  color: "#333333",
};

const SelectionCheckbox = ({ attachment, selected, onToggle }) => (
  <Checkbox
    size="small"
    checked={selected}
    onChange={onToggle}
    inputProps={{ "aria-label": `Selecionar ${attachment.fileName}` }}
  />
);

// the short DD/MM/AA stays visual; screen readers get the unambiguous long date
const DateText = ({ value }) =>
  value ? (
    <>
      <span aria-hidden="true">{formatCultureHubDate(value)}</span>
      <Box component="span" sx={visuallyHiddenSx}>
        {formatDateLongPtBr(value)}
      </Box>
    </>
  ) : (
    formatCultureHubDate(value)
  );

export default CultureHubRow;